import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import type { CourtPriceDateItem, CourtRemainingWindowsView } from '@/lib/venue/priceTypes'
import {
  toPriceDateSummary,
  type PriceBandRowFormState,
  type PriceBandRowsErrors,
} from '@/lib/venue/priceViewModel'

import { PriceBandEditor } from './PriceBandEditor'

const APPLIED_FROM_LABEL: Record<string, string> = {
  DATE: '이 날짜 규칙',
  DAY_TYPE: '요일 유형 규칙',
  BASE_FEE: '기본 요금',
}

/**
 * 공휴일·특정 날짜 요금(요구 2). 목록 + 한 날짜의 편집 폼 + **지금 무엇이 먹고 있는지** 안내 패널.
 *
 * 🚨 상태는 갖지 않는다 — 저장 오케스트레이션(검증·확인 모달·일괄 적용)은 화면이 소유한다.
 * 🚨 한 번에 **한 날짜만** 편집한다(날짜 다중 선택을 만들지 않는다 — 요청 수가 코트×날짜로 곱해진다, R5).
 */
export function PriceDateEditor({
  dates,
  isLoading,
  selectedDate,
  onSelectDate,
  minDate,
  label,
  onLabelChange,
  rows,
  onRowsChange,
  errors,
  disabled,
  maxRows,
  unitMinutes,
  remaining,
  onDelete,
}: {
  dates: CourtPriceDateItem[]
  isLoading: boolean
  selectedDate: string
  onSelectDate: (date: string) => void
  /** KST 오늘(응답 `baseTime` 기준). 과거 날짜는 서버가 400 으로 거부한다(설계 D7). */
  minDate: string
  label: string
  onLabelChange: (label: string) => void
  rows: PriceBandRowFormState[]
  onRowsChange: (rows: PriceBandRowFormState[]) => void
  errors?: PriceBandRowsErrors
  disabled: boolean
  maxRows: number
  /** 🔒 코트의 예약 단위(`reservationUnitMinutes`) — `PriceBandEditor`에 그대로 전달한다(`#876`). */
  unitMinutes: number
  remaining: CourtRemainingWindowsView | undefined
  onDelete: (date: string) => void
}) {
  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title="설정된 날짜"
          description="오늘부터 90일 안에 등록된 날짜 요금이에요. 날짜 규칙은 평일·주말 요금보다 우선합니다."
        />
        <CardBody className="space-y-2">
          {isLoading ? <LoadingState rows={2} label="날짜 요금을 불러오는 중" /> : null}
          {!isLoading && dates.length === 0 ? (
            <EmptyState
              title="설정된 날짜가 없습니다"
              description="공휴일처럼 요금이 다른 날짜를 아래에서 추가해 보세요."
            />
          ) : null}
          {dates.map((item) => (
            <div
              key={item.date}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slateish-200 px-3 py-2.5"
            >
              <div>
                <p className="text-sm font-medium text-ink-700">
                  {item.date}
                  {item.label ? ` · ${item.label}` : ''}
                </p>
                <p className="text-xs text-slateish-500">{toPriceDateSummary(item)}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={disabled}
                  onClick={() => {
                    onSelectDate(item.date)
                  }}
                >
                  {`${item.date} 수정`}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="border-danger-500 text-danger-500 hover:bg-danger-100"
                  disabled={disabled}
                  onClick={() => {
                    onDelete(item.date)
                  }}
                >
                  {`${item.date} 삭제`}
                </Button>
              </div>
            </div>
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="날짜 요금 설정" />
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="price-date-input"
              >
                날짜
              </label>
              <Input
                id="price-date-input"
                type="date"
                min={minDate}
                disabled={disabled}
                value={selectedDate}
                onChange={(event) => {
                  onSelectDate(event.target.value)
                }}
              />
              <p className="mt-1 text-xs text-slateish-500">오늘 이후 날짜만 설정할 수 있어요.</p>
            </div>
            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="price-date-label"
              >
                이름 (선택)
              </label>
              <Input
                id="price-date-label"
                placeholder="예) 설날"
                disabled={disabled}
                value={label}
                onChange={(event) => {
                  onLabelChange(event.target.value)
                }}
              />
            </div>
          </div>

          {selectedDate ? (
            <>
              {remaining ? (
                <div className="rounded-lg bg-slateish-100 px-3 py-2.5">
                  <p className="text-xs font-medium text-ink-700">
                    {remaining.fullyCovered
                      ? '이 날짜는 전 시간대가 날짜 규칙으로 덮여 있어요.'
                      : '아직 이 날짜 규칙이 없는 시간대'}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {remaining.windows.map((window) => (
                      <li key={`${window.startTime}-${window.endTime}`} className="text-xs text-slateish-600">
                        {window.startTime}~{window.endTime} · 지금은{' '}
                        {APPLIED_FROM_LABEL[window.appliedFrom] ?? window.appliedFrom}(
                        {window.appliedHourlyFee.toLocaleString('ko-KR')}원)이 적용돼요
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <PriceBandEditor
                axisLabel={selectedDate}
                rows={rows}
                onChange={onRowsChange}
                disabled={disabled}
                maxRows={maxRows}
                unitMinutes={unitMinutes}
                errors={errors}
              />
              <p className="text-xs text-slateish-500">
                구간을 모두 비우고 저장하면 이 날짜 규칙이 해제되고 평일·주말 요금으로 돌아가요.
              </p>
            </>
          ) : (
            <>
              <p className="text-xs text-slateish-500">날짜를 먼저 선택해 주세요.</p>
              {/* 날짜 미선택·과거 날짜처럼 구간 편집기가 없을 때의 오류도 여기서 받아 준다. */}
              {errors?.general ? <p className="text-xs text-danger-500">{errors.general}</p> : null}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
